'use client'
import Define from './define';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { db } from '@/services/Database';
import { setDefineData } from '@/redux/slices/define/defineSlice';
import { showSuccessToast, showErrorToast } from "@/components/toast/toast";

interface OfflineDefineProps {
  projectId?: string;
  isOffline?: boolean;
  onSuccess?: (projectId: string) => void;
}

export default function OfflineDefine({
  projectId,
  isOffline,
  onSuccess
}: OfflineDefineProps) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const handleOfflineSubmit = async (values: any) => {
    try {
      const timestamp = new Date().toISOString();

      if (values.type === 'create') {
        // Create project transaction
        const result = await db.transaction('rw', [db.projects, db.shippingModes], async () => {
          // Create project
          const project = await db.projects.add({
            projectName: values.projectDetails.projectName,
            projectDescription: values.projectDetails.projectDescription,
            createdBy: 'Offline User',
            target: 2,
            percentage: 0.7,
            plusByUser: 6,
            createdAt: timestamp,
            updatedAt: timestamp,
            overallDecision: 'pending'
          });

          // Create shipping modes
          const createdShippingModes = [];
          if (values.shippingModes?.length > 0) {
            for (const mode of values.shippingModes) {
              const shippingMode = await db.shippingModes.add({
                ...mode,
                projectId: project,
                createdAt: timestamp,
                updatedAt: timestamp
              });
              createdShippingModes.push(shippingMode);
            }
          }

          return { project, shippingModes: createdShippingModes };
        });

        showSuccessToast("Project created successfully");
        if (onSuccess) {
          onSuccess(result.project.toString());
        }
      } else if (values.type === 'update' && projectId) {
        // Update project transaction
        await db.transaction('rw', [db.projects, db.shippingModes], async () => {
          const project = await db.getProject(parseInt(projectId));
          if (!project) {
            throw new Error('Project not found');
          }

          // Update project
          await db.projects.update(parseInt(projectId), {
            projectName: values.projectDetails.projectName,
            projectDescription: values.projectDetails.projectDescription,
            updatedAt: timestamp
          });

          // Update shipping modes
          if (values.shippingModes?.length > 0) {
            for (const mode of values.shippingModes) {
              if (mode.id) {
                // Update existing shipping mode
                await db.shippingModes.update(mode.id, {
                  ...mode,
                  updatedAt: timestamp
                });
              } else {
                // Create new shipping mode
                await db.shippingModes.add({
                  ...mode,
                  projectId: parseInt(projectId),
                  createdAt: timestamp,
                  updatedAt: timestamp
                });
              }
            }
          }
        });

        showSuccessToast("Project updated successfully");
      }
    } catch (error) {
      console.error('Error saving project:', error);
      showErrorToast("Failed to save project");
    }
  };

  useEffect(() => {
    const loadProject = async () => {
      if (projectId) {
        try {
          setIsLoading(true);
          const project = await db.getProject(parseInt(projectId));

          if (project) {
            // Get shipping modes for project
            const shippingModes = await db.shippingModes
              .where('projectId')
              .equals(parseInt(projectId))
              .toArray();
            console.log(shippingModes,'shipping modes')
            dispatch(setDefineData({
              type: 'update',
              projectId: project.id,
              projectDetails: {
                projectName: project.projectName,
                projectDescription: project.projectDescription
              },
              shippingModes: shippingModes || [],
              users: [] // Offline mode doesn't handle users
            }));
          }
        } catch (error) {
          console.error('Error loading project:', error);
          showErrorToast('Failed to load project data');
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId, dispatch]);

  // if (isLoading) {
  //   return <div>Loading project data...</div>;
  // }

  return (
    <Define
      projectId={projectId}
      isOffline={isOffline}
      onOfflineSubmit={handleOfflineSubmit}
    />
  );
}
